import { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { type HelpAnchor, HELP_SECTIONS, HELP_SECTION_TITLES, HELP_TOC } from '../help/manifest';

interface HelpDrawerProps {
  open: boolean;
  /** Current section being shown. `null` falls back to the
   *  top-level `'index'` content — the drawer always has SOMETHING
   *  to show when open. */
  anchor: HelpAnchor | null;
  /** Drawer open / close — wired to the hook's `setOpen`. */
  onOpenChange: (open: boolean) => void;
  /** TOC click handler — wired to the hook's `navigate`. */
  onNavigate: (anchor: HelpAnchor) => void;
}

/** Right-side `Sheet` that hosts the help docs. The canvas stays
 *  visible alongside the drawer (no modal overlay) so the user can
 *  read instructions while interacting with what they're learning
 *  about. The drawer's width respects the viewport at narrow
 *  widths but caps at ~520px on desktop — wide enough to read
 *  prose comfortably without burying the canvas. */
export function HelpDrawer({ open, anchor, onOpenChange, onNavigate }: HelpDrawerProps) {
  const effectiveAnchor: HelpAnchor = anchor ?? 'index';
  const content = HELP_SECTIONS[effectiveAnchor];
  const title = HELP_SECTION_TITLES[effectiveAnchor];

  // Reset the article's scroll position to the top whenever the
  // user navigates to a different section. Without this, a long
  // section the user had scrolled to the bottom of would leave
  // the next section's content out of view — the user would see
  // a blank area until they scrolled up. Keyed off the effective
  // anchor so an explicit open at a specific section also lands
  // at the top.
  const articleRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (articleRef.current) articleRef.current.scrollTop = 0;
  }, [effectiveAnchor]);

  // Stable memo so React-Markdown's tree doesn't rebuild on every
  // parent render (the editor re-renders frequently). The drawer is
  // mounted-but-hidden when closed, so this also keeps content
  // ready for the next open without re-parsing.
  const markdownNode = useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Intercept in-content links of the form `#panels/foo` or
          // `#tools/bar` — instead of triggering a URL hash nav (which
          // would force the page to scroll + leave a `#` in the
          // address bar), drive the drawer's `navigate` directly.
          // External links (non-`#` hrefs) fall through to a standard
          // `<a target="_blank">`. Typo'd in-content anchors render
          // as visibly-broken text rather than escaping to a new tab
          // — content authors notice the breakage in dev, and the
          // user sees a clear "this is unwired" affordance instead
          // of a confusing tab open.
          a: ({ href, children, ...rest }) => {
            if (href && href.startsWith('#')) {
              const target = href.slice(1) as HelpAnchor;
              if (target in HELP_SECTIONS) {
                return (
                  <button
                    type="button"
                    className="text-primary hover:underline inline"
                    onClick={() => onNavigate(target)}
                  >
                    {children}
                  </button>
                );
              }
              return (
                <span
                  className="text-destructive line-through cursor-help"
                  title={`Broken help link: no section "#${target}"`}
                >
                  {children}
                </span>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    ),
    [content, onNavigate],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col"
        // Defaults to the Sheet's modal-overlay behavior — the
        // backdrop intercepts canvas clicks while the drawer is
        // open. Users dismiss via Esc, click outside, or the X.
        // A future enhancement could make it non-blocking
        // (always-visible "drawer" rather than a modal sheet),
        // but v1 keeps the standard pattern for keyboard accessibility.
      >
        <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
          <SheetTitle className="text-sm font-medium">{title}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 min-h-0">
          <nav
            aria-label="Help sections"
            className="w-44 shrink-0 border-r border-border overflow-y-auto py-2"
          >
            {HELP_TOC.map((group) => (
              <div key={group.category} className="mb-2 last:mb-0">
                {/* Category header — visually distinct from the
                    clickable entries below it. The user shouldn't
                    try to click these (they're not actionable —
                    they're section dividers). The bg tint + bold
                    weight + border + role="heading" all reinforce
                    that these are labels, not navigation targets. */}
                <div
                  role="heading"
                  aria-level={3}
                  className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground bg-muted/60 border-y border-border select-none"
                >
                  {group.label}
                </div>
                {group.entries.map((entry) => {
                  const isActive = entry.anchor === effectiveAnchor;
                  return (
                    <button
                      key={entry.anchor}
                      type="button"
                      onClick={() => onNavigate(entry.anchor)}
                      aria-current={isActive ? 'page' : undefined}
                      className={[
                        'w-full text-left pl-4 pr-3 py-1.5 text-xs',
                        isActive
                          ? 'bg-accent text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                      ].join(' ')}
                    >
                      {entry.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
          <article
            ref={articleRef}
            className="flex-1 overflow-y-auto px-6 py-4 prose prose-sm dark:prose-invert max-w-none"
            // The prose-* classes give the markdown headings, lists,
            // paragraphs, and code blocks readable typography out of
            // the box. max-w-none overrides Tailwind Typography's
            // default 65ch — the drawer is already narrow.
          >
            {markdownNode}
          </article>
        </div>
      </SheetContent>
    </Sheet>
  );
}
